import React, { FC, useState } from "react";
import Image from "next/image";
import PackageCard from "components-shared/PackageCard";
import PackageCardContent from "components-shared/PackageCardContent";
import {
  Package,
  PackageConfig,
} from "components/ContentfulRenderer/ContentfulPackages/types";
import { AmountToPay } from "utils/insurance/getAmountToPay";
import { formatCurrency } from "utils/currency";
import { getOrg, isOrg } from "utils/site";
import { useWebTranslation, Trans } from "translations";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import { FlexCol, FlexRow } from "ui-kit/Layout/Flex";
import styles from "./InsurancePackage.module.css";
import Colors from "ui-kit/colors";
import { Button } from "ui-kit/Button";
import { ProductEnum } from "../../../../../__generated__/globalTypes";
import { Modal } from "ui-kit";

type InsurancePackageProps = {
  style?: any;
  onPress?: () => void;
  onCompare: () => void;
  active?: boolean;
  packageConfig: PackageConfig;
  pack: Package;
  amountToPay?: AmountToPay;
  numberOfHighlightItems?: number;
  numberOfVisibleItems?: number;
  showMarketing?: boolean;
  isCopyExperiment?: boolean;
};

type ListItemProps = {
  title: string;
  description?: string;
  value?: string | boolean;
  isCopyExperiment?: boolean;
};

const ListItem: FC<ListItemProps> = ({
  title,
  description,
  value,
  isCopyExperiment,
}) => {
  const { t } = useWebTranslation();
  const [showInfo, setShowInfo] = useState(false);

  return (
    <li
      style={{ listStyle: "none" }}
      aria-label={`${
        value ? t("global.included") : t("global.not_included")
      }: ${t(title)}`}
      tabIndex={0}
    >
      <FlexCol style={{ marginBottom: 8 }}>
        <FlexRow alignItems="center">
          <Image
            src={
              value
                ? "/images/icons/checkmark-green.svg"
                : "/images/icons/close-red.svg"
            }
            alt=""
            width={20}
            height={20}
            style={{ display: "flex", marginTop: 4 }}
          />
          <Gutter horizontal size={1} />
          {isCopyExperiment ? (
            <Typography style={{ fontSize: 16 }}>{t(title)}</Typography>
          ) : (
            <div
              onClick={
                description
                  ? (e) => {
                      e?.stopPropagation();
                      setShowInfo(true);
                    }
                  : () => null
              }
            >
              <Typography
                color={value ? "black" : "grey500"}
                style={
                  description
                    ? {
                        textDecoration: "underline",
                        textDecorationStyle: "dotted",
                        textDecorationColor: Colors.grey400,
                        textUnderlineOffset: 3,
                        cursor: "pointer",
                      }
                    : {}
                }
              >
                {t(title)}
              </Typography>
            </div>
          )}
        </FlexRow>
        {typeof value === "string" && (
          <>
            <Gutter size={1} />
            <FlexRow>
              <div style={{ height: 20, width: 20, flexShrink: 0 }}></div>
              <Gutter horizontal size={0.5} />
              <Typography color="grey500" style={{ whiteSpace: "pre-wrap" }}>
                {t(value)}
              </Typography>
            </FlexRow>
          </>
        )}
      </FlexCol>
      <Modal
        title={t(title)}
        isOpen={showInfo}
        onClose={() => setShowInfo(false)}
        width="small"
      >
        <Typography style={{ whiteSpace: "pre-wrap" }}>
          {t(description ?? "")}
        </Typography>
      </Modal>
    </li>
  );
};

type ItemsProps = {
  items: { title: string; description?: string; value?: string | boolean }[];
  visible: boolean;
  isCopyExperiment?: boolean;
};

const commonCopyExperimentItems = (
  species: ProductEnum
): Array<{
  title: string;
  value: boolean;
}> => {
  const org = getOrg();

  switch (org) {
    case "swe":
      return [
        { title: "copy_exp.common.1", value: true },
        { title: "copy_exp.common.2", value: true },
        { title: "copy_exp.common.3", value: true },
        { title: "copy_exp.common.4", value: true },
        {
          title: `copy_exp.common.5_${species}`,
          value: true,
        },
        { title: "copy_exp.common.6", value: true },
      ];
    case "fra":
      return [
        { title: "copy_exp.common.1", value: true },
        { title: "copy_exp.common.2", value: true },
        { title: "copy_exp.common.3", value: true },
        { title: "copy_exp.common.4", value: true },
        {
          title: `copy_exp.common.5_${species}`,
          value: true,
        },
        { title: "copy_exp.common.6", value: true },
        { title: "copy_exp.common.9", value: true },
      ];

    default:
      return [];
  }
};

const copyExperiementPackageItems = (
  limit: number
): Array<{ title: string; value: boolean | string; description?: string }> => {
  const org = getOrg();

  if (org === "swe") {
    switch (limit) {
      case 3000000: {
        return [{ title: "copy_exp.common.7", value: true }];
      }
      case 6000000:
      case 16000000: {
        return [
          { title: "copy_exp.common.7", value: true },
          {
            title: "copy_exp.common.8",
            value: true,
          },
        ];
      }
      default: {
        return [];
      }
    }
  }

  if (org === "fra") {
    return [{ title: "copy_exp.common.7", value: true }];
  }

  if (org === "ger") {
    switch (limit) {
      case 0:
        return [
          {
            title: "insurance_packages.experiment.common.1",
            description: "ger.insurance_packages.legend.operations.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.common.2",
            value: true,
          },
          {
            title: "insurance_packages.experiment.common.3",
            value: true,
          },
          { title: "insurance_packages.experiment.op.1", value: true },
          { title: "insurance_packages.experiment.common.5", value: true },
        ];
      case 300000:
        return [
          {
            title: "insurance_packages.experiment.common.1",
            description: "ger.insurance_packages.legend.operations.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.mini.1",
            description:
              "ger.insurance_packages.legend.benefit_limit.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.mini.2",
            description:
              "ger.insurance_packages.legend.annual_pension_budget.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.common.2",
            value: true,
          },
          {
            title: "insurance_packages.experiment.common.3",
            value: true,
          },
          { title: "insurance_packages.experiment.common.4", value: true },
          { title: "insurance_packages.experiment.common.5", value: true },
        ];
      case 100000:
        return [
          {
            title: "insurance_packages.experiment.common.1",
            description: "ger.insurance_packages.legend.operations.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.midi.1.title",
            description:
              "ger.insurance_packages.legend.benefit_limit.description",
            value: "insurance_packages.experiment.midi.1.description",
          },
          {
            title: "insurance_packages.experiment.midi.2",
            description:
              "ger.insurance_packages.legend.annual_pension_budget.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.common.2",
            value: true,
          },
          {
            title: "insurance_packages.experiment.common.3",
            value: true,
          },
          { title: "insurance_packages.experiment.common.4", value: true },
          { title: "insurance_packages.experiment.common.5", value: true },
        ];

      case 9999999900:
        return [
          {
            title: "insurance_packages.experiment.common.1",
            description: "ger.insurance_packages.legend.operations.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.maxi.1.title",
            description:
              "ger.insurance_packages.legend.benefit_limit.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.maxi.2",
            description:
              "ger.insurance_packages.legend.annual_pension_budget.description",
            value: true,
          },
          {
            title: "insurance_packages.experiment.common.2",
            value: true,
          },
          {
            title: "insurance_packages.experiment.common.3",
            value: true,
          },
          { title: "insurance_packages.experiment.common.4", value: true },
          { title: "insurance_packages.experiment.common.5", value: true },
        ];

      default:
        [];
    }
  }

  return [];
};

const InsurancePackage = ({
  style,
  onPress,
  onCompare,
  active,
  packageConfig,
  pack,
  amountToPay,
  numberOfHighlightItems = 0,
  showMarketing,
  isCopyExperiment,
  numberOfVisibleItems = isCopyExperiment ? 8 : showMarketing ? 6 : 3,
}: InsurancePackageProps) => {
  const { t } = useWebTranslation();

  const items = isCopyExperiment
    ? commonCopyExperimentItems(
        packageConfig.product === ProductEnum.liability
          ? ProductEnum.dog
          : packageConfig.product
      )
    : showMarketing && pack.marketing?.usps
    ? pack.marketing.usps
    : packageConfig.benefits.map((benefit) => ({
        title: benefit.title,
        description: benefit.description,
        value: pack.benefitIncludes[benefit.name],
      }));

  return (
    <section
      role="group"
      aria-label={t("global.insurance_package") + " " + t(pack.title)}
      onClick={onPress}
      className={styles.packageContainer}
      style={{
        ...style,
        cursor: onPress ? "pointer" : "inherit",
        borderColor: active ? Colors.blue300 : "white",
        backgroundColor: active ? Colors.navy100 : "white",
      }}
    >
      <PackageCard
        pack={pack}
        amountToPay={amountToPay}
        showMarketing={showMarketing}
      />
      <PackageCardContent>
        <ul style={{ padding: 0, margin: 0 }}>
          {items.slice(0, numberOfHighlightItems).map((item) => (
            <li key={item.title}>
              <Typography variant="body3">
                <strong>{t(item.title)}</strong>
              </Typography>
              {typeof item.value === "string" && (
                <Typography variant="body1" color="grey500">
                  <strong>{t(item.value as string)}</strong>
                </Typography>
              )}
            </li>
          ))}
          {isCopyExperiment &&
            copyExperiementPackageItems(pack.limit).map((item, index) => {
              return (
                <li key={item.title} style={{ display: "flex" }}>
                  <div style={{ display: "flex", marginBottom: 10 }}>
                    {isOrg("ger") ? (
                      <ListItem
                        title={item.title}
                        description={item.description}
                        value={item.value}
                      />
                    ) : (
                      <>
                        <Image
                          src={"/images/icons/checkmark-green.svg"}
                          alt=""
                          width={20}
                          height={20}
                          style={{
                            display: "flex",
                            marginTop: 4,
                            marginRight: 8,
                          }}
                        />
                        <Typography style={{ fontSize: 16 }}>
                          <Trans
                            i18nKey={item.title}
                            values={{ limit: formatCurrency(pack.limit / 100) }}
                            components={[<strong key="0" />]}
                          />
                        </Typography>
                      </>
                    )}
                  </div>

                  {index ===
                    copyExperiementPackageItems(pack.limit).length - 1 && (
                    <div
                      style={{
                        height: 1,
                        backgroundImage: `linear-gradient(to right, ${Colors.grey300} 30%, rgba(255, 255, 255, 0) 0%)`,
                        backgroundSize: "8px",
                        marginBottom: 4,
                      }}
                    />
                  )}
                </li>
              );
            })}

          {numberOfHighlightItems > 0 && <Gutter size={2} />}
          {items
            .slice(numberOfHighlightItems, numberOfVisibleItems)
            .map((item) => {
              return (
                <ListItem
                  key={item.title}
                  isCopyExperiment={isCopyExperiment}
                  {...item}
                />
              );
            })}
        </ul>
        <div style={{ marginTop: "auto" }} />
        <Gutter size={2} />
        {!isCopyExperiment && (
          <div
            style={{
              height: 1,
              backgroundImage: `linear-gradient(to right, ${Colors.grey300} 30%, rgba(255, 255, 255, 0) 0%)`,
              backgroundSize: "8px",
            }}
          />
        )}
        <Gutter size={2} />
        <FlexRow
          alignItems="center"
          justifyContent="center"
          onClick={(e) => {
            onCompare();
            e?.stopPropagation();
          }}
          style={{ padding: "8px 16px" }}
        >
          <button style={{ all: "initial" }}>
            <Typography
              variant="body3"
              color="grey500"
              style={{ cursor: "pointer" }}
            >
              <strong>{t("insurance_packages.all_benefits")}</strong>
            </Typography>
          </button>
        </FlexRow>
        {onPress && (
          <>
            <Gutter size={1} />
            <Button
              onPress={() => onPress()}
              title={active ? t("global.selected") : t("global.select")}
              variant={active ? "outlined" : "custom"}
              color={active ? "white" : "blue100"}
              aria-pressed={active}
              textColor="blue300"
              iconFirst
              icon={
                active && (
                  <Image
                    src="/images/icons/checkmark-blue.svg"
                    alt={t("global.yes")}
                    width={20}
                    height={20}
                  />
                )
              }
            />
          </>
        )}
      </PackageCardContent>
    </section>
  );
};

export default InsurancePackage;
